import { Button } from "@mui/material";
import { FaPlusCircle } from "react-icons/fa";
import {
  ADMISSION_MODULE_LOCKED_MESSAGE,
  entryEffectiveDue,
  admissionRowHasProtectedLinks,
} from "../accountUtils";
import AccountModuleEntryCard from "./AccountModuleEntryCard";
import { AccountEmptyState, AccountPanelHeader } from "./accountEditorUi";

const AccountAdmissionPanel = ({
  rows = [],
  onSelectEntry,
  onAddEntry,
  onDelete,
  /** Opens the admission record itself — where the entry is edited and deleted. */
  onOpenExternal,
  onToggleNotes,
  /** Opens EyeModal with this admission's course / placement details. */
  onOpenCourseDetails,
  /** Navigate to the linked admission application. */
  onOpenAdmission,
  getCompletionContext,
  isCompleted,
  hydrateAdmissionValues,
  /** Extra per-row actions (withdraw / settle) — supplied by the editor, which owns those dialogs. */
  renderRowActions,
}) => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.paid += Number(row.paidAmount) || 0;
      acc.due += Number(row.dueAmount) || 0;
      acc.commission += Number(row.commission) || 0;
      acc.profit += Number(row.profitLoss) || 0;
      return acc;
    },
    { paid: 0, due: 0, commission: 0, profit: 0 }
  );

  return (
    <div className="space-y-4">
      <AccountPanelHeader
        title="Admissions"
        count={rows.length}
        action={
          <Button variant="outlined" startIcon={<FaPlusCircle />} onClick={onAddEntry}>
            Add admission
          </Button>
        }
      />

      {rows.length === 0 ? (
        <AccountEmptyState
          title="No admissions on this account yet"
          description="Add an admission that already has a term schedule. You can then record payments, commission and bonus by term."
          actionLabel="Add admission"
          onAction={onAddEntry}
          actionIcon={<FaPlusCircle />}
        />
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => {
            const entryNumber = i + 1;
            const values = hydrateAdmissionValues?.(row.admissionValues || {}) || {};
            const ctx = getCompletionContext(row);
            const completed = isCompleted(row, ctx);
            const locked = admissionRowHasProtectedLinks(row);
            const terms = (row.admissionDetails || []).length || 0;
            const detailLine = [values.university, values.intake].filter(Boolean).join(" · ");
            // An admission belongs to the Admissions module: from here you open it, you do not
            // delete it. A row with no admission behind it has nowhere to open, so it keeps the
            // delete that is its only way off the account.
            const admissionId = row.admissionValues?.studentDbId;

            return (
              <AccountModuleEntryCard
                key={row.uuid}
                index={entryNumber}
                title={values.course || values.university || `Admission ${entryNumber}`}
                subtitle={detailLine || `Admission ${entryNumber}`}
                meta={[
                  { label: "Status", value: row.admissionStatus || values.status },
                  { label: "Terms", value: String(terms) },
                ]}
                agreed={row.agreedAmount}
                paid={row.paidAmount}
                due={row.dueAmount}
                effectiveDue={entryEffectiveDue(row)}
                cost={row.cost}
                commission={row.commission}
                showCommission={Number(row.commission) > 0}
                profitLoss={row.profitLoss}
                completed={completed}
                pendingCount={ctx.pendingVerificationCount || 0}
                onOpen={() => onSelectEntry(row.uuid)}
                openLabel="Manage terms"
                onTitleClick={onOpenCourseDetails ? () => onOpenCourseDetails(row) : undefined}
                onOpenCourseDetails={
                  onOpenCourseDetails ? () => onOpenCourseDetails(row) : undefined
                }
                courseDetailsLabel="Course details"
                onOpenAdmission={onOpenAdmission ? () => onOpenAdmission(row) : undefined}
                onOpenNotes={onToggleNotes ? () => onToggleNotes(row) : undefined}
                onOpenExternal={
                  admissionId && onOpenExternal ? () => onOpenExternal(row) : undefined
                }
                onDelete={admissionId ? undefined : () => onDelete?.(row)}
                deleteDisabled={locked}
                deleteTitle={locked ? ADMISSION_MODULE_LOCKED_MESSAGE : "Delete entry"}
                actions={renderRowActions?.(row)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountAdmissionPanel;
